import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import UpdateBanner from './components/UpdateBanner';
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt';
import SubdomainRedirect from './components/SubdomainRedirect';
import GlobalEscBack from './components/GlobalEscBack';
import GlobalZoomPan from './components/GlobalZoomPan';
import GlobalKeyboardShortcuts from './components/GlobalKeyboardShortcuts';
import { useGlobalModalKbNav } from './hooks/useGlobalModalKbNav';

// Pages
import CustomerLogin from './pages/CustomerLogin';
import HandlerLogin from './pages/HandlerLogin';
import AdminLogin from './pages/AdminLogin';
import AdminAuthCallback from './pages/AdminAuthCallback';
import CleanSlateCallback from './pages/CleanSlateCallback';
import MBCallback from './pages/MBCallback';
import AdminForgotPassword from './pages/AdminForgotPassword';
import AdminPasswordResetCallback from './pages/AdminPasswordResetCallback';
import CustomerSelect from './pages/CustomerSelect';
import CustomerDashboardLayout from './pages/CustomerDashboardLayout';
import CustomerChat from './pages/CustomerChat';
import CustomerDashboard from './pages/CustomerDashboard';
import CustomerGroupList from './pages/CustomerGroupList';
import HandlerDashboard from './pages/HandlerDashboard';
import HandlerSettings from './pages/HandlerSettings';
import CallingHandlerDashboard from './pages/CallingHandlerDashboard';
import CallingHandlerSettings from './pages/CallingHandlerSettings';
import HandlerChat from './pages/HandlerChat';
import HandlerProfile from './pages/HandlerProfile';
import AdminDashboard from './pages/AdminDashboard';
import AdminSettings from './pages/AdminSettings';
import AdminActivityDashboard from './pages/AdminActivityDashboard';
import InactiveClientsTracker from './pages/InactiveClientsTracker';
import NewMembersPage from './pages/NewMembersPage';
import ClientsByIP from './pages/ClientsByIP';
import EditClient from './pages/EditClient';
import CreateClient from './pages/CreateClient';
import AdminChat from './pages/AdminChat';
import ClientList from './pages/ClientList';
import BroadcastPage from './pages/BroadcastPage';
import GroupListPage from './pages/GroupListPage';
import CreateGroupPage from './pages/CreateGroupPage';
import GroupChatPage from './pages/GroupChatPage';
import GroupDashboard from './pages/GroupDashboard';
import AddGroupMembers from './pages/AddGroupMembers';
import InquiryChat from './pages/InquiryChat';
import AdminChangePassword from './pages/AdminChangePassword';
import AdminChangeMobile from './pages/AdminChangeMobile';
import AdminChangeEmail from './pages/AdminChangeEmail';
import SupportAnalytics from './pages/SupportAnalytics';
import MBConfig from './pages/MBConfig';
import ServiceTimelines from './pages/ServiceTimelines';

import './App.css';

// Protected Route Component - Role-aware redirect to prevent login page flash
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-semibold text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    // Role-aware redirect based on URL path to prevent wrong login page flash
    const pathname = window.location.pathname;
    if (pathname.startsWith('/admin')) {
      return <Navigate to="/admin-login" replace />;
    } else if (pathname.startsWith('/handler')) {
      return <Navigate to="/handler-login" replace />;
    }
    return <Navigate to="/customer-login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate login based on current user's role
    const userRole = user.role;
    if (userRole === 'super_admin') {
      return <Navigate to="/admin-login" replace />;
    } else if (['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'calling_handler'].includes(userRole)) {
      return <Navigate to="/handler-login" replace />;
    }
    return <Navigate to="/customer-login" replace />;
  }

  return children;
};

// Role-based redirect for PWA start_url (/?role=admin, /?role=handler, /?role=customer)
const RoleBasedRedirect = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const role = urlParams.get('role');
  
  if (role === 'admin') return <Navigate to="/admin-login" />;
  if (role === 'handler') return <Navigate to="/handler-login" />;
  return <Navigate to="/customer-login" />;
};

// Direct Chat Link Components (No login required initially)
const DirectChatLink = ({ chatType }) => {
  const { user } = useAuth();
  
  if (!user) {
    // Redirect to customer login with chat type
    return <Navigate to={`/customer-login?redirect=${chatType}`} />;
  }
  
  return <Navigate to={`/customer-chat?type=${chatType}`} />;
};

function NotificationNavigator() {
  // Fix-batch-4 #1 — when a background/closed-app notification is clicked, the
  // Service Worker focuses this window and posts NOTIFICATION_NAVIGATE with the
  // correct role-based path; route there via React Router (no full reload).
  const navigate = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMsg = (event) => {
      if (event.data && event.data.type === 'NOTIFICATION_NAVIGATE' && event.data.path) {
        try { navigate(event.data.path); }
        catch { try { window.location.assign(event.data.path); } catch {} }
      }
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [navigate]);
  return null;
}

function MaintenanceGate() {
  // Point 9 (New Points) — full-screen maintenance overlay for the chosen
  // audience. super_admin is never blocked. Polls status every 20s + on focus.
  const [maint, setMaint] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/maintenance/status`);
        const d = await res.json();
        if (alive) setMaint(d && d.active ? d : null);
      } catch { /* ignore */ }
    };
    poll();
    const iv = setInterval(poll, 20000);
    const onFocus = () => poll();
    window.addEventListener('focus', onFocus);
    return () => { alive = false; clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, []);

  if (!maint) return null;
  let role = '';
  try { role = (JSON.parse(localStorage.getItem('dws_user') || '{}').role || ''); } catch { /* ignore */ }
  if (role === 'super_admin' || role === 'admin') return null; // admin never blocked
  const isHandler = role.endsWith('_handler') || role === 'handler';
  const isCustomer = role === 'customer';
  const audience = maint.audience || 'handlers';
  // audience 'handlers' → only handlers; 'customers' → only customers (+ logged-out),
  // handlers NEVER blocked; 'all' → handlers + customers (+ logged-out).
  let blocked;
  if (audience === 'all') blocked = isHandler || isCustomer || role === '';
  else if (audience === 'customers') blocked = isCustomer || role === '';
  else blocked = isHandler;
  if (!blocked) return null;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 100000 }}
      className="flex items-center justify-center bg-slate-900/95 backdrop-blur-sm p-6"
      data-testid="maintenance-overlay"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        <div className="text-5xl mb-4">🛠️</div>
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Under Maintenance</h1>
        <p className="text-slate-600 whitespace-pre-line" data-testid="maintenance-message">{maint.message}</p>
        {maint.ends_at && (
          <p className="text-xs text-slate-400 mt-4">
            Expected back around {new Date(maint.ends_at).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })} IST
          </p>
        )}
      </div>
    </div>
  );
}

function AppRoutes() {
  useGlobalModalKbNav();
  return (
    <>
      <SubdomainRedirect />
      <NotificationNavigator />
      <NotificationPermissionPrompt />
      <UpdateBanner />
      <MaintenanceGate />
      <GlobalEscBack />
      <GlobalKeyboardShortcuts />
      <Routes>
        {/* Login Pages */}
        <Route path="/customer-login" element={<CustomerLogin />} />
        <Route path="/handler-login" element={<HandlerLogin />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin-auth-callback" element={<AdminAuthCallback />} />
        <Route path="/admin-clean-slate-callback" element={<CleanSlateCallback />} />
        <Route path="/admin-mb-callback" element={<MBCallback />} />
        <Route path="/admin-forgot-password" element={<AdminForgotPassword />} />
        <Route path="/admin-password-reset-callback" element={<AdminPasswordResetCallback />} />

        {/* Short URL aliases for direct access */}
        <Route path="/admin" element={<Navigate to="/admin-login" replace />} />
        <Route path="/handler" element={<Navigate to="/handler-login" replace />} />
        <Route path="/customer" element={<Navigate to="/customer-login" replace />} />
        <Route path="/login" element={<Navigate to="/customer-login" replace />} />

      {/* Direct Chat Links */}
      <Route path="/deposit" element={<DirectChatLink chatType="deposit" />} />
      <Route path="/withdraw" element={<DirectChatLink chatType="withdrawal" />} />
      <Route path="/support" element={<DirectChatLink chatType="support" />} />

      {/* Inquiry Chat (No login required) */}
      <Route path="/inquiry-chat" element={<InquiryChat />} />

      {/* Admin Profile Update Routes */}
      <Route
        path="/admin-change-password"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminChangePassword />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-change-mobile"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminChangeMobile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-change-email"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminChangeEmail />
          </ProtectedRoute>
        }
      />

      {/* Customer Routes */}
      <Route
        path="/customer-select"
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerDashboardLayout />
          </ProtectedRoute>
        }
      />
      {/* CustomerChat - No auth protection, handles inquiry guests internally */}
      <Route
        path="/customer-chat"
        element={<CustomerChat />}
      />
      <Route
        path="/customer-dashboard"
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerDashboard />
          </ProtectedRoute>
        }
      />

      {/* Customer Groups List */}
      <Route
        path="/customer/groups"
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerGroupList />
          </ProtectedRoute>
        }
      />

      {/* Customer Group Chat */}
      <Route
        path="/customer/group/:groupId"
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <GroupChatPage />
          </ProtectedRoute>
        }
      />


      {/* Handler Routes */}
      <Route
        path="/handler-dashboard"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler']}>
            <HandlerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/handler-settings"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler']}>
            <HandlerSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/handler-chat/:roomId"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'calling_handler']}>
            <HandlerChat />
          </ProtectedRoute>
        }
      />

      {/* Point 48 — Calling Handler's own stripped-down routes */}
      <Route
        path="/calling-handler-dashboard"
        element={
          <ProtectedRoute allowedRoles={['calling_handler']}>
            <CallingHandlerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calling-handler-settings"
        element={
          <ProtectedRoute allowedRoles={['calling_handler']}>
            <CallingHandlerSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/handler-profile/:customerId"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'calling_handler', 'super_admin']}>
            <HandlerProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-list"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'calling_handler', 'super_admin']}>
            <ClientList />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin-dashboard"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-settings"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/support-analytics"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SupportAnalytics />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/mb-config"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <MBConfig />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/service-timelines"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler']}>
            <ServiceTimelines />
          </ProtectedRoute>
        }
      />
      {/* Session-47: handler-specific alias so back-nav lands on handler settings */}
      <Route
        path="/handler/service-timelines"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'calling_handler']}>
            <ServiceTimelines />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/clients-by-ip"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <ClientsByIP />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/edit-client"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <EditClient />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/create-client"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <CreateClient />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/handler/create-client"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'calling_handler']}>
            <CreateClient />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-activity"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminActivityDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inactive-clients"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'deposit_handler']}>
            <InactiveClientsTracker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/new-members"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler']}>
            <NewMembersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-chat/:roomId"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminChat />
          </ProtectedRoute>
        }
      />

      {/* Broadcast Routes (Handler + Admin) */}
      <Route
        path="/broadcast"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'calling_handler', 'super_admin']}>
            <BroadcastPage />
          </ProtectedRoute>
        }
      />

      {/* Group Routes (Handler + Admin) */}
      <Route
        path="/groups"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'super_admin']}>
            <GroupListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/create"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'super_admin']}>
            <CreateGroupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/:groupId"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'super_admin']}>
            <GroupChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/:groupId/dashboard"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'super_admin']}>
            <GroupDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/:groupId/add-members"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'super_admin']}>
            <AddGroupMembers />
          </ProtectedRoute>
        }
      />

      {/* Default Route - Role-aware redirect for PWA */}
      <Route path="/" element={<RoleBasedRedirect />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <GlobalZoomPan>
          <AppRoutes />
        </GlobalZoomPan>
        <Toaster position="top-center" richColors />
        <PWAInstallPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
